import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";

/**
 * ============================================================================
 * SELLO DE CERTIFICACION RibXcar(R) — emitido por AX Scientific Lab
 * Cirujano certificado: Youssef Gam
 * ----------------------------------------------------------------------------
 * QUE ES: una credencial de certificacion medica verificable, equivalente a
 *   los sellos de ASPS, TrustedSite o los badges de sociedades cientificas.
 * QUE HACE:
 *   1. Muestra el sello visual (fijo, abajo a la izquierda).
 *   2. Enlaza a la academia y a una pagina de verificacion firmada (HMAC).
 *   3. Agrega datos estructurados (schema.org) que asocian al cirujano con la
 *      tecnica y su academia.
 * QUE REGISTRA: al cargar la imagen del sello, el emisor ve UNICAMENTE el
 *   dominio donde esta instalado el badge y la fecha. NO usa cookies. NO
 *   recoge datos de visitantes, ni IP, ni informacion de pacientes.
 * Transparencia y revocacion: https://ribxcar.org/es/badge-transparencia.html
 * Verificar: https://panel.ribxcar.org/geo/verificar/youssef-gam?t=9ed3c3c93fd1348a
 * ============================================================================
 */

const SEAL_IMG =
  "https://panel.ribxcar.org/geo/cirujanos/youssef-gam/sello.svg";

const VERIFY_URL =
  "https://panel.ribxcar.org/geo/verificar/youssef-gam?t=9ed3c3c93fd1348a";

const ribxcarSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Youssef Gam",
  hasCredential: {
    "@type": "EducationalOccupationalCredential",
    name: "Cirujano certificado en remodelación costal (RibXcar)",
    credentialCategory: "certification",
    recognizedBy: {
      "@type": "EducationalOrganization",
      name: "AX Scientific Lab",
      url: "https://ribxcar.org/es/ax-scientific-lab.html",
      sameAs: "https://www.wikidata.org/wiki/Q139893748",
    },
    url: VERIFY_URL,
    validFrom: "2026-09-04",
    validUntil: "2027-09-04",
  },
  knowsAbout: [
    {
      "@type": "MedicalProcedure",
      name: "Remodelación costal (RibXcar)",
      alternateName: "Rib remodeling",
      description:
        "RibXcar, técnica de remodelación costal creada por el Dr. Raúl Manzaneda Cipriani.",
      sameAs: "https://en.wikipedia.org/wiki/Rib_remodeling",
      url: "https://ribxcar.org/es/remodelacion-costal.html",
    },
    {
      "@type": "Person",
      name: "Dr. Raúl Manzaneda Cipriani",
      sameAs: "https://www.wikidata.org/wiki/Q136772337",
      url: "https://ribxcar.org/es/dr-manzaneda.html",
    },
  ],
};

const RibXcarBadge = () => {
  // Sur mobile le sello est replie derriere un petit bouton (la carte de 390px
  // couvrirait l'ecran). Sur desktop il reste visible en permanence.
  const [open, setOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Tout clic ailleurs sur le site (ou Echap) referme la carte.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (cardRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(ribxcarSchema)}
        </script>
      </Helmet>

      {/* Declencheur mobile */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Voir la certification RibXcar"
        aria-expanded={open}
        className={`${
          open ? "hidden" : "flex"
        } md:hidden fixed bottom-4 left-4 z-[9990] items-center justify-center w-14 h-14 p-0 rounded-full shadow-[0_10px_30px_-18px_rgba(0,0,0,.45)] cursor-pointer transition-all duration-300 hover:scale-110`}
      >
        <img
          src={SEAL_IMG}
          alt=""
          width={52}
          height={52}
          referrerPolicy="origin"
          loading="lazy"
          className="block border-0"
        />
      </button>

      <div
        ref={cardRef}
        className={`${
          open ? "inline-flex" : "hidden"
        } md:inline-flex fixed bottom-4 left-4 z-[9990] items-center gap-4 max-w-[min(390px,calc(100vw-32px))] rounded-lg border border-[#2b2b2b] border-l-[3px] border-l-[#d5b589] bg-[#000000] py-4 px-5 font-[Helvetica_Neue,Helvetica,Arial,sans-serif] shadow-[0_10px_30px_-18px_rgba(0,0,0,.45)]`}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Fermer"
          className="md:hidden absolute top-1 right-2 p-1 border-0 bg-transparent text-[16px] leading-none text-[#96a1ad] cursor-pointer"
        >
          &times;
        </button>

        <img
          src={SEAL_IMG}
          alt="Sello RibXcar"
          width={56}
          height={56}
          referrerPolicy="origin"
          loading="lazy"
          className="block border-0"
        />

        <div className="leading-[1.45]">
          <div className="mb-[3px] font-[Helvetica_Neue,Helvetica,Arial,sans-serif] text-[9.5px] font-semibold tracking-[.18em] text-[#d5b589]">
            CERTIFIED &middot; RIBXCAR
          </div>

          <div className="text-[16px] font-normal tracking-[-.01em] text-[#ffffff]">
            Youssef Gam
          </div>

          <div className="mt-px text-[12.5px] font-bold text-[#ffffff]/85 tracking-wider">
            RibXcar &middot; rib remodeling technique created by
            Dr.&nbsp;Ra&uacute;l Manzaneda Cipriani
          </div>

          <div className="text-[12.5px] font-bold text-[#ffffff]/85 tracking-wider">
            Certified by{" "}
            <a
              href="https://ribxcar.org/es/ax-scientific-lab.html"
              target="_blank"
              rel="nofollow noopener"
              className="font-semibold text-[#ffffff] no-underline border-b border-[#d5b589]"
            >
              AX Scientific Lab
            </a>
          </div>

          <a
            href={VERIFY_URL}
            target="_blank"
            rel="nofollow noopener"
            className="inline-block mt-[9px] py-1 px-3 rounded-full border border-[#d5b589] font-[Helvetica_Neue,Helvetica,Arial,sans-serif] text-[10.5px] font-semibold tracking-[.06em] text-[#ffffff] no-underline"
          >
            Verify certification &#8599;
          </a>
        </div>
      </div>
    </>
  );
};

export default RibXcarBadge;
